import { useCallback, useEffect, useState } from 'react';
import type { FormEventHandler } from 'react';
import { Button, Divider, Form, FormActions, Modal } from '@zillow/constellation-10';
import { useRouter } from 'next/router';
import { emitRequestZillowPayments } from '../../../analytics/events';
import { redirectToLogin, useAuth } from '../../../AuthProvider';
import RequestPaymentsBody from './RequestPaymentsBody';
import EmailSentBody from './EmailSentBody';
import { useYourRentalWebApiHandler_sendRentPaymentRequest } from '@zg-rentals/ts-your-rental-api-client-react-query';
import { client } from '../../../rentalClient';
import { validate } from 'email-validator';
import { useYourRentalPageContext } from '../../../contexts/YourRentalPageContext';

type RequestZillowPaymentsModalCtaProps = {
  ctaText: string;
  clickstreamTriggerObject: string;
  authTriggerSource: string;
  listingAlias?: string;
};

const CONTENT = {
  SEND_REQUEST_BUTTON: 'Send request to landlord',
  OKAY_BUTTON: 'Okay',
  EMAIL_VALIDATION_ERROR: 'Enter a valid email address',
} as const;

const RequestZillowPaymentsModalCta = ({
  ctaText,
  clickstreamTriggerObject,
  authTriggerSource,
  listingAlias,
}: RequestZillowPaymentsModalCtaProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const { renterProfile } = useYourRentalPageContext();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailSendError, setEmailSendError] = useState<boolean>(false);
  const [emailSending, setEmailSending] = useState<boolean>(false);
  const [emailValidationError, setEmailValidationError] = useState<string>('');

  const { mutate: sendRentPaymentRequest } = useYourRentalWebApiHandler_sendRentPaymentRequest({
    httpClient: client,
    onSuccess: () => {
      setEmailSent(true);
      setEmailSendError(false);
      setEmailSending(false);
    },
    onError: () => {
      setEmailSendError(true);
      setEmailSending(false);
    },
  });

  // Auto-open modal after login if query param is present
  useEffect(() => {
    if (router.query.showPaymentModal === clickstreamTriggerObject && user?.isLoggedIn) {
      emitRequestZillowPayments(renterProfile);
      setIsOpen(true);

      // Clean up query params
      const { showPaymentModal: _showPaymentModal, auth_trigger: _auth_trigger, ...restQuery } = router.query;
      router.replace(
        {
          pathname: router.pathname,
          query: restQuery,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [router.query, user, clickstreamTriggerObject, router.pathname, router, renterProfile]);

  const handleOpenChange = useCallback((open: boolean) => {
    setEmailSendError(false);
    setEmailValidationError('');
    setIsOpen(open);
    if (!open) {
      setEmail('');
      setEmailSent(false);
    }
  }, []);

  const handleOpenModal = useCallback(() => {
    emitRequestZillowPayments(renterProfile);

    if (!user?.isLoggedIn) {
      const redirectParams = new URLSearchParams({
        showPaymentModal: clickstreamTriggerObject,
        auth_trigger: authTriggerSource,
      });
      return redirectToLogin(`${location.pathname}?${redirectParams}`);
    }

    setIsOpen(true);
  }, [user?.isLoggedIn, clickstreamTriggerObject, authTriggerSource, renterProfile]);

  const resetEmail = useCallback(() => {
    setEmail('');
    setEmailSent(false);
  }, []);

  const validateEmail = useCallback((emailToValidate: string): boolean => {
    if (!validate(emailToValidate)) {
      setEmailValidationError(CONTENT.EMAIL_VALIDATION_ERROR);
      return false;
    }
    setEmailValidationError('');
    return true;
  }, []);

  const handleEmailChange = useCallback(
    (newEmail: string) => {
      setEmail(newEmail);
      // Clear validation error when user starts typing
      if (emailValidationError) {
        setEmailValidationError('');
      }
    },
    [emailValidationError],
  );

  const onSendEmailClicked: FormEventHandler = useCallback(
    (event) => {
      event.preventDefault();

      if (!validateEmail(email)) {
        return;
      }

      setEmailSending(true);
      sendRentPaymentRequest({
        postData: { email, listingAlias },
      });
    },
    [email, listingAlias, sendRentPaymentRequest, validateEmail],
  );

  const renderModalBody = useCallback(() => {
    if (emailSent) {
      return <EmailSentBody email={email} onResetEmail={resetEmail} />;
    }

    return (
      <RequestPaymentsBody
        emailSendError={emailSendError}
        email={email}
        onEmailChange={handleEmailChange}
        emailValidationError={emailValidationError}
      />
    );
  }, [emailSent, email, resetEmail, emailSendError, handleEmailChange, emailValidationError]);

  const renderModalFooter = useCallback(() => {
    if (emailSent) {
      return (
        <Button tone="brand" emphasis="filled" onClick={() => handleOpenChange(false)} aria-label="Close modal">
          {CONTENT.OKAY_BUTTON}
        </Button>
      );
    }

    return (
      <FormActions css={{ marginBlockStart: 'auto !important' }}>
        <Button
          loading={emailSending}
          tone="brand"
          emphasis="filled"
          disabled={!email || !!emailValidationError}
          type="submit"
          data-testid="send-request-to-landlord-button"
          aria-label="Send request to landlord"
        >
          {CONTENT.SEND_REQUEST_BUTTON}
        </Button>
      </FormActions>
    );
  }, [emailSent, emailSending, email, emailValidationError, handleOpenChange]);

  return (
    <>
      <Button
        emphasis="filled"
        size="sm"
        data-testid="request-zillow-payments-button"
        onClick={handleOpenModal}
        aria-label="Request Zillow payments"
      >
        {ctaText}
      </Button>

      <Modal.Root open={isOpen} onOpenChange={handleOpenChange} size={{ base: 'fullScreen', md: 'md' }}>
        <Modal.Portal>
          <Modal.Backdrop
            css={{
              alignItems: { base: undefined, md: 'flex-start' },
              paddingBlockStart: { base: undefined, md: 'layout.looser' },
            }}
          >
            <Modal.Content asChild>
              <Form onSubmit={onSendEmailClicked} noValidate>
                <Divider />
                <Modal.Body css={{ padding: { base: '{spacing.default}', md: '{spacing.looser}' } }}>
                  {renderModalBody()}
                </Modal.Body>
                <Divider />
                <Modal.Footer>{renderModalFooter()}</Modal.Footer>
                <Modal.CloseButton />
              </Form>
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
};

export default RequestZillowPaymentsModalCta;
